/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention */
import { useOrderConversion } from '~/composables/tag/useOrderConversion';
import { DISCOUNT_NAME, PRODUCT_PARENT_CATEGORY_LIST } from '~/constants';
import { nonNullable } from '~/utils';
import { CartSchema } from '~/types/modules/cart';
import { DistributionCourseInfo, OrderSimulations } from '~/types/modules/order';
import { CartType } from '~/enums';
import { useStore } from '../useStore';

/** eコマースのitemsの型 */
type ECommerceItem = {
  item_name: string | undefined;
  item_id: string;
  price?: number;
  item_category?: string;
  item_list_name?: string;
};

export const useGoogleTag = () => {
  const store = useStore();
  const { orderedDetails } = useOrderConversion();

  /** 一般商品の注文開始イベント */
  const setGoogleOrderStartTag = (cart: CartSchema, simulationRes: OrderSimulations | null) => {
    if (!simulationRes) return;

    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'JPY',
          value: simulationRes.price_set.item_total_amount,
          coupon: simulationRes.cart_discounts.find((discount) => discount.name === DISCOUNT_NAME.FIRST_TIME)
            ? '初回送料無料'
            : undefined,
          items: cart?.items?.map((item) => ({
            item_name: item.product_names.find(({ lang }) => lang === 'ja')?.name,
            item_id: item.product_code,
            price:
              simulationRes.item_discounts.find((discount) => discount.product_variant_id === item.product_variant_id)
                ?.applied_discount_price_including_tax || item.price,
            item_category: PRODUCT_PARENT_CATEGORY_LIST.find(
              (category) => category.id === item.product_category_id.toString()
            )?.label,
            quantity: item.quantity,
          })),
        },
      });
    }
  };

  /** 頒布会の注文開始イベント */
  const setGoogleDistributionOrderStartTag = async (distributionCourseId: number | null) => {
    const simulatedData = store.state.modules.order.distributeSimulationRes;
    if (!simulatedData || !distributionCourseId) return;

    try {
      await store.dispatch('modules/order/fetchDistributionInfo', { distributionCourseId });
    } catch {
      return;
    }
    const courseInfo = store.state.modules.order.distributionCourseInfo;

    if (window.dataLayer && courseInfo) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'JPY',
          value: simulatedData.price_set.item_total_amount,
          items: [
            {
              item_name: courseInfo.product_name_s,
              item_id: undefined,
              price: courseInfo.lowest_price_general_i,
              item_category: '頒布会',
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  /** 一般商品の購入イベント */
  const setGoogleConversionTag = () => {
    const {
      simulatedData,
      orderId,
      cart,
      userOption: { shipments },
      giftOption,
    } = orderedDetails.value;

    if (!simulatedData || !orderId) {
      return;
    }

    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: orderId,
          value: simulatedData.price_set.item_total_amount,
          tax:
            simulatedData.price_set.price_per_tax_rate_set['8'].tax_amount +
            simulatedData.price_set.price_per_tax_rate_set['10'].tax_amount,
          shipping: simulatedData.price_set.shipping_cost,
          currency: 'JPY',
          coupon: simulatedData.cart_discounts.find((discount) => discount.name === DISCOUNT_NAME.FIRST_TIME)
            ? '初回送料無料'
            : undefined,
          items: cart?.items?.map((item) => ({
            item_name: item.product_names.find(({ lang }) => lang === 'ja')?.name,
            item_id: item.product_code,
            affiliation: cart?.cart_name,
            price:
              simulatedData.item_discounts.find((discount) => discount.product_variant_id === item.product_variant_id)
                ?.applied_discount_price_including_tax || item.price,
            item_category: PRODUCT_PARENT_CATEGORY_LIST.find(
              (category) => category.id === item.product_category_id.toString()
            )?.label,
            quantity: item.quantity,
          })),
        },
      });

      shipments.forEach(({ giftWrappings = [], paperBags = {} }, index) => {
        window.dataLayer.push({
          event: 'gift_setting',
          event_action: 'ギフト設定',
          event_category: giftWrappings.length > 0 ? '希望する' : '希望しない',
          event_label: 'ギフト・ラッピング',
        });

        giftWrappings.forEach(({ giftBoxId, hasMessage, noshi, selectedWines, wrappingType }) => {
          /** 選択したギフトラッピング　 */
          const selectedGiftWrapping = simulatedData.shipments[index].gift_box_set.find(({ id }) => id === giftBoxId);

          if (selectedGiftWrapping) {
            window.dataLayer.push({
              event: 'gift_setting',
              event_action: 'ギフト設定',
              event_category: selectedGiftWrapping.name,
              event_label: 'ギフトボックス',
            });
          }

          window.dataLayer.push({
            event: 'gift_setting',
            event_action: 'ギフト設定',
            event_category: wrappingType === 'ribbon' ? 'リボン' : wrappingType === 'noshi' ? 'のし' : 'なし',
            event_label: 'リボン・のし',
          });

          /** 選択した熨斗 */
          const selectedNoshi = giftOption.find(({ id }) => id === noshi.id);

          if (wrappingType === 'noshi' && selectedNoshi) {
            window.dataLayer.push({
              event: 'gift_setting',
              event_action: 'ギフト設定',
              event_category: selectedNoshi.name,
              event_label: 'のしの種類',
            });
          }

          window.dataLayer.push({
            event: 'gift_setting',
            event_action: 'ギフト設定',
            event_category: hasMessage ? '利用する' : '利用しない',
            event_label: 'メッセージカード',
          });

          /** ラッピングした商品の「商品名：数量」の配列 */
          const selectedItems = selectedWines
            .map(({ id, count }) => {
              const item = cart?.items.find(({ product_variant_id }) => product_variant_id === id);
              const itemName = item?.product_names.find(({ lang }) => lang === 'ja')?.name;
              return itemName && count ? `${itemName}：${count}` : undefined;
            })
            .filter(nonNullable);

          selectedItems.forEach((item) => {
            window.dataLayer.push({
              event: 'gift_setting',
              event_action: 'ギフト設定',
              event_category: item,
              event_label: 'セットする商品',
            });
          });
        });

        /** 選択した紙袋の「紙袋名：数量」の配列 */
        const selectedPaperBags = Object.entries(paperBags)
          .map(([paperBagId, quantity]) => {
            /** 選択した紙袋 */
            const paperBag = simulatedData.shipments[index].paper_bags.find(({ id }) => id === Number(paperBagId));
            return paperBag && quantity ? `${paperBag.name}：${quantity}` : undefined;
          })
          .filter(nonNullable);

        selectedPaperBags.forEach((paperBag) => {
          window.dataLayer.push({
            event: 'gift_setting',
            event_action: 'ギフト設定',
            event_category: paperBag,
            event_label: '紙袋',
          });
        });
      });
    }
  };

  /** 頒布会の購入イベント */
  const setGoogleDistributionConversionTag = (item: DistributionCourseInfo | null, subscriptionId: number | null) => {
    const simulatedData = store.state.modules.order.distributeSimulationRes;
    if (!simulatedData || !item || !subscriptionId) return;

    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: subscriptionId,
          value: simulatedData.price_set.item_total_amount,
          tax:
            (simulatedData.price_set.price_per_tax_rate_set['8']?.tax_amount || 0) +
            (simulatedData.price_set.price_per_tax_rate_set['10']?.tax_amount || 0),
          shipping: 0, // 頒布会は送料無料
          currency: 'JPY',
          items: [
            {
              item_name: item.product_name_s,
              item_id: undefined,
              affiliation: CartType.DISTRIBUTION,
              price: item.lowest_price_general_i,
              item_category: '頒布会',
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  /** 一般商品の商品閲覧イベント */
  const setGoogleViewTag = (value: number, item: ECommerceItem) => {
    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: 'JPY',
          value,
          items: [
            {
              quantity: 1,
              ...item,
            },
          ],
        },
      });
    }
  };

  /**
   * 一般商品のカートインイベント
   * @param value - 商品の単価（税抜）
   * @param item - eコマースの商品情報
   * @param type - add_cart用のイベントラベル
   */
  const setGoogleCartTag = (value: number, item: ECommerceItem, type?: string) => {
    const { item_id, item_list_name, ...items } = item;

    if (window.dataLayer) {
      if (type) {
        window.dataLayer.push({
          event: 'tlb.add_cart',
          event_action: 'add_cart',
          event_label: type,
          item_list: [item_id],
          event_category: item_list_name,
        });
      }

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'JPY',
          value,
          items: [
            {
              item_id,
              item_list_name,
              quantity: 1,
              ...items,
            },
          ],
        },
      });
    }
  };

  return {
    setGoogleOrderStartTag,
    setGoogleDistributionOrderStartTag,
    setGoogleConversionTag,
    setGoogleDistributionConversionTag,
    setGoogleViewTag,
    setGoogleCartTag,
  };
};
/* eslint-enable @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention */
